import { EventEmitter, useMount } from '@apia/util';
import { ReactNode, ReactPortal, useDeferredValue, useState } from 'react';
import { PanelContainer } from '../types';
import { DefaultPanelWrapper } from '../splash/panels/DefaultPanelWrapper';
import { DashboardPanelsBootstraper } from './DashboardPanelsBootstraper';
import { DashboardContext } from './DashboardContext';
import { DashboardPanel } from './DashboardPanel';
import { DragAndDrop } from '../splash/util/DragAndDrop';
import { eventsController } from '../eventsController';
import { Router } from './DashboardRouter';
import { importContainer } from '../util';

export class Dashboard extends EventEmitter<{
  changeScene: string;
}> {
  bootstrapper = new DashboardPanelsBootstraper(this);
  panels: Record<string, DashboardPanel> | null = null;
  router = new Router();
  scenes: Record<string, string[]> = {};

  #currentScene = '/';
  #dragAndDrop = new DragAndDrop();
  #PanelContainer: PanelContainer = DefaultPanelWrapper;
  #panelsRenderedPortals: ReactPortal[] = [];

  getPanel = (id: string) => this.panels?.[id] as DashboardPanel;
  getPanelByName = (name: string) =>
    Object.values(this.panels ?? {}).find(
      (current) => current.props.name === name,
    );

  #loadPanels = () => {
    this.panels = {};

    const panelsElements = document.querySelectorAll(
      '[data-panel]',
    ) as NodeListOf<HTMLElement>;

    Array.from(panelsElements).forEach((current) => {
      let container = this.#PanelContainer;

      const customContainer = current.dataset.panelContainer;
      if (customContainer) {
        container = importContainer(current.dataset.panelContainer!);
      }

      const panel = new DashboardPanel({
        dashboard: this,
        PanelContainer: container,
        id: current.dataset.id as string,
      });

      let panelPath = (panel.getParameters().PAR_SPA_PATH as string) || '*';
      if (panelPath === '/') panelPath = `/${this.urlContext}`;
      if (panelPath !== '*') panel.hide();
      else panel.show();

      const paths = panelPath.split(',');
      paths.forEach((currentPath) => {
        if (!this.scenes[currentPath]) this.scenes[currentPath] = [];
        this.scenes[currentPath].push(panel.props.id);
      });

      (this.panels as Record<string, DashboardPanel>)[panel.props.id] = panel;

      this.#panelsRenderedPortals.push(panel.portal);
    });

    this.#dragAndDrop.bootstrap();
  };

  #Context = ({ children }: { children: ReactNode }) => {
    return (
      <DashboardContext.Provider value={this}>
        {children}
      </DashboardContext.Provider>
    );
  };

  #suscribeToEvents = () => {
    eventsController.onEvent({ eventType: 'dashboard__focus' }, () => {
      Object.values(this.panels ?? {}).forEach((current) => {
        if (current.props.refreshOnHome) {
          void current.refresh();
        }
      });
    });
  };
  #usePanelsList = () => {
    const [panels, setPanels] = useState<ReactNode>(null);
    const deferredPanels = useDeferredValue(panels);

    useMount(() => {
      this.loadScene();
      setPanels(this.#panelsRenderedPortals);
    });

    this.router.useSwitch([
      {
        route: new RegExp(`(?:/spa)?/(\\w+)`),
        callback: (result) => {
          this.loadScene(result[1]);
        },
      },
      {
        route: new RegExp(`/`),
        callback: () => {
          this.loadScene('/');
        },
      },
    ]);

    return deferredPanels;
  };

  constructor(
    panelsContainer: PanelContainer = DefaultPanelWrapper,
    public urlContext: string,
  ) {
    super();

    this.#currentScene = `/`;
    this.#PanelContainer = panelsContainer ?? this.#PanelContainer;
    this.#suscribeToEvents();
  }

  #SceneRenderer = () => {
    const Context = this.#Context;
    const panels = this.#usePanelsList();

    return <Context>{panels}</Context>;
  };

  loadScene = (scene: string = this.#currentScene): ReactPortal[] => {
    let actualScene = scene;
    if (!actualScene.startsWith('/')) actualScene = `/${actualScene}`;

    if (!this.panels) this.#loadPanels();
    if (!this.scenes[actualScene]) this.scenes[actualScene] = [];

    this.scenes[this.#currentScene].forEach((scenePanelId) => {
      if (!this.scenes[actualScene].includes(scenePanelId))
        this.panels?.[scenePanelId]?.hide();
    });
    this.scenes[actualScene].forEach((scenePanelIds) => {
      const panel = this.panels?.[scenePanelIds];
      if (panel) {
        panel.show();
        if (panel.hasInited && panel.props.refreshOnStart) void panel.refresh();
      }
    });

    this.#currentScene = actualScene;

    return this.#panelsRenderedPortals;
  };

  Component = () => {
    const R = this.#SceneRenderer;
    return <R />;
  };
}
